import { type RefObject, useEffect } from 'react';
import { usePivotGlowStore } from './pivot-glow-store';

type Options = {
  scrollRef: RefObject<HTMLElement | null>;
  /**
   * Optional virtualized-list adapter. Return true if the adapter scrolled the
   * product into view; the hook will skip its DOM-query fallback. Return false
   * to defer to the fallback (e.g. when the product isn't in the visible set).
   */
  scrollToProduct?: (productId: string) => boolean;
};

/**
 * Scrolls the currently-glowing product (from the pivot-glow store) into view.
 *
 * Default strategy: query `[data-row-id="<productId>"]` inside `scrollRef` and
 * call `scrollIntoView`. Works for both windowed (TanStackTable) and plain DOM
 * lists since virtualized rows still set the attribute when rendered.
 *
 * Virtualizer strategy: when the row hasn't been rendered yet, the DOM query
 * misses. Pass `scrollToProduct` to resolve the product → index and call the
 * virtualizer's own `scrollToIndex` instead.
 */
export function useScrollToProduct({ scrollRef, scrollToProduct }: Options) {
  const glowingId = usePivotGlowStore((state) => state.glowingId);

  useEffect(() => {
    if (!glowingId) {
      return;
    }

    if (scrollToProduct?.(glowingId)) {
      return;
    }

    const node = scrollRef.current?.querySelector<HTMLElement>(`[data-row-id="${glowingId}"]`);
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [glowingId, scrollRef, scrollToProduct]);
}
